import { useEffect, useState, useCallback } from 'react';
import { Calendar, momentLocalizer, View } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'moment/locale/tr';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../../hooks/useStore';
import { IAppointment } from '../../../../models/appointment/AppointmentModel';
import styles from './AppointmentCalendar.module.css';

moment.locale('tr');
const localizer = momentLocalizer(moment);

interface CalendarEvent {
  title: string;
  start: Date;
  end: Date;
  resource?: IAppointment;
}

const AppointmentCalendar = () => {
  const { appointmentStore, popupStore } = useStore();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<View>('month');

  const loadAppointments = useCallback(async () => {
    try {
      const result = await appointmentStore.listAppointments({
        startRow: 0,
        endRow: 1000000,
        filterModel: null,
        sortModel: null,
      });

      const calendarEvents: CalendarEvent[] = result.rows
        .map((apt: IAppointment): CalendarEvent | null => {
          if (!apt.appointmentDate || !apt.appointmentStartTime || !apt.appointmentEndTime) {
            return null;
          }

          const dateStr = apt.appointmentDate;
          const startTimeStr = apt.appointmentStartTime;
          const endTimeStr = apt.appointmentEndTime;

          const startMoment = moment(`${dateStr} ${startTimeStr}`, 'YYYY-MM-DD HH:mm');
          const endMoment = moment(`${dateStr} ${endTimeStr}`, 'YYYY-MM-DD HH:mm');

          return {
            title: `${apt.personName || 'Bilinmeyen'} - ${apt.hospitalName || ''}`,
            start: startMoment.toDate(),
            end: endMoment.toDate(),
            resource: apt,
          };
        })
        .filter((event): event is CalendarEvent => event !== null);

      setEvents(calendarEvents);
    } catch (error) {
      console.error('Error loading appointments for calendar:', error);
    }
  }, [appointmentStore]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  const handleSelectEvent = useCallback(
    (event: CalendarEvent) => {
      if (event.resource) {
        appointmentStore.setSelectedAppointmentToView(event.resource);
        popupStore.setIsAppointmentDetailOpen(true);
      }
    },
    [appointmentStore, popupStore]
  );

  const handleNavigate = useCallback((newDate: Date) => {
    setCurrentDate(newDate);
  }, []);

  const handleViewChange = useCallback((newView: View) => {
    setView(newView);
  }, []);

  const eventStyleGetter = useCallback(
    (event: CalendarEvent) => {
      return {
        className: styles.calendarEvent,
        style: {
          backgroundColor: '#2563eb',
          borderColor: '#1e40af',
          color: 'white',
        },
      };
    },
    []
  );

  return (
    <div className={styles.calendarContainer}>
      <div className={styles.calendarWrapper}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%' }}
          onSelectEvent={handleSelectEvent}
          onNavigate={handleNavigate}
          onView={handleViewChange}
          view={view}
          date={currentDate}
          eventPropGetter={eventStyleGetter}
          messages={{
            next: 'Sonraki',
            previous: 'Önceki',
            today: 'Bugün',
            month: 'Ay',
            week: 'Hafta',
            day: 'Gün',
            agenda: 'Ajanda',
            date: 'Tarih',
            time: 'Saat',
            event: 'Randevu',
            noEventsInRange: 'Bu aralıkta randevu bulunmamaktadır.',
          }}
          culture="tr"
        />
      </div>
    </div>
  );
};

export default observer(AppointmentCalendar);

